import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import { useStateContext } from "contexts/ContextProvider";
import { usePageTitle } from "hooks/usePageTitle";
import api from "services/api";
import Overlay from "components/Overlay/Overlay";

/**
 * Document Categories manager overlay.
 *
 * Opened from Settings → Service Documents (category icon button). Follows the
 * standard CE overlay shell (same structure as ServiceDocumentNewEdit):
 * full-screen dimmer → rounded panel → header with back arrow + title →
 * scrollable body on `background.alt`.
 *
 * Categories classify service documents (e.g. COT) and feed the Category
 * dropdown in the Service Document New/Edit overlay. CRUD lives in
 * server/routes/ServiceDocuments.js (/serviceDocuments/categories).
 */
const DocumentCategoriesManager = () => {
  usePageTitle("Document Categories");

  const theme = useTheme();
  const { setOverlayName, showSnackbar, openDialog, closeDialog } =
    useStateContext();

  const closeOverlay = () => setOverlayName("");

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [addForm, setAddForm] = useState({
    Name: "",
    Description: "",
    SortOrder: "",
  });

  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({
    Name: "",
    Description: "",
    SortOrder: "",
  });

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get("/serviceDocuments/categories");
      const list = Array.isArray(response.data?.categories)
        ? response.data.categories
        : [];
      setCategories(list);
    } catch (err) {
      console.error("Error fetching document categories:", err);
      showSnackbar("Unable to load document categories", theme, "error");
    } finally {
      setLoading(false);
    }
  }, [showSnackbar, theme]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const handleCreate = async () => {
    const name = addForm.Name.trim();
    if (!name) {
      showSnackbar("Category name is required", theme, "error");
      return;
    }

    setSaving(true);
    try {
      await api.post("/serviceDocuments/categories", {
        Name: name,
        Description: addForm.Description?.trim() || null,
        SortOrder: addForm.SortOrder === "" ? 0 : Number(addForm.SortOrder),
      });
      setAddForm({ Name: "", Description: "", SortOrder: "" });
      showSnackbar("Document category created", theme, "success");
      fetchCategories();
    } catch (err) {
      showSnackbar(
        err?.response?.data?.error || "Error creating document category",
        theme,
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (category) => {
    setEditingId(category.id);
    setEditForm({
      Name: category.name || "",
      Description: category.description || "",
      SortOrder: category.sortOrder ?? 0,
    });
  };

  const handleSaveEdit = async (categoryId) => {
    const name = editForm.Name.trim();
    if (!name) {
      showSnackbar("Category name is required", theme, "error");
      return;
    }

    setSaving(true);
    try {
      await api.put(`/serviceDocuments/categories/${categoryId}`, {
        Name: name,
        Description: editForm.Description?.trim() || null,
        SortOrder: editForm.SortOrder === "" ? 0 : Number(editForm.SortOrder),
      });
      setEditingId(null);
      showSnackbar("Document category updated", theme, "success");
      fetchCategories();
    } catch (err) {
      showSnackbar(
        err?.response?.data?.error || "Error updating document category",
        theme,
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (category) => {
    openDialog({
      theme,
      title: "Delete Document Category",
      message: `Are you sure you want to delete "${category.name}"? Documents in this category will show as unclassified.`,
      primaryAction: "Delete",
      secondaryAction: "Cancel",
      primaryOnClick: async () => {
        try {
          await api.put(`/serviceDocuments/categories/${category.id}`, {
            IsDeleted: true,
          });
          closeDialog();
          showSnackbar("Document category deleted", theme, "success");
          fetchCategories();
        } catch (err) {
          closeDialog();
          showSnackbar(
            err?.response?.data?.error || "Error deleting document category",
            theme,
            "error",
          );
        }
      },
      secondaryOnClick: closeDialog,
    });
  };

  const cardSx = {
    bgcolor: theme.palette.background.default,
    border: `1px solid ${theme.palette.primary.border2}`,
    borderRadius: "10px",
    p: 1.5,
    transition: "all 0.2s ease",
  };

  // Visible field borders — the default outlined border is too faint against
  // the card background, so give the fields a clear border + hover/focus states.
  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: theme.palette.background.default,
      "& fieldset": {
        borderColor: theme.palette.primary.border2,
        borderWidth: 1,
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
        borderWidth: 2,
      },
    },
  };

  return (
    <Overlay
      id="document-categories-manager"
      open={true}
      onClose={closeOverlay}
      title="Document Categories"
      maxWidthPx={1300}
    >
      <Box
        sx={{
          p: "1rem 2.3rem 1rem 2.5rem",
          width: "100%",
        }}
      >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2 }}
          >
            Categories classify service documents (e.g. COT) and feed the
            Category dropdown when uploading or editing a document.
          </Typography>

          {/* Add category */}
          <Box
            sx={{
              ...cardSx,
              display: "flex",
              gap: 1,
              alignItems: "center",
              flexWrap: "wrap",
              mb: 2,
            }}
          >
            <TextField
              size="small"
              label="Name"
              value={addForm.Name}
              onChange={(event) =>
                setAddForm((prev) => ({ ...prev, Name: event.target.value }))
              }
              sx={{ flex: "1 1 160px", ...fieldSx }}
            />
            <TextField
              size="small"
              label="Description (optional)"
              value={addForm.Description}
              onChange={(event) =>
                setAddForm((prev) => ({
                  ...prev,
                  Description: event.target.value,
                }))
              }
              sx={{ flex: "2 1 220px", ...fieldSx }}
            />
            <TextField
              size="small"
              label="Sort"
              type="number"
              value={addForm.SortOrder}
              onChange={(event) =>
                setAddForm((prev) => ({
                  ...prev,
                  SortOrder: event.target.value,
                }))
              }
              sx={{ width: 90, ...fieldSx }}
            />
            <Button
              variant="contained"
              startIcon={<AddRoundedIcon />}
              onClick={handleCreate}
              disabled={saving || !addForm.Name.trim()}
            >
              Add
            </Button>
          </Box>

          <Divider sx={{ my: 2 }} />

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress />
            </Box>
          ) : categories.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                p: 3,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                No categories yet — add one above.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {categories.map((category) => (
                <Box
                  key={category.id}
                  sx={{
                    ...cardSx,
                    "&:hover": {
                      bgcolor: theme.palette.action.hover,
                      borderColor: theme.palette.primary.main,
                    },
                  }}
                >
                  {editingId === category.id ? (
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        alignItems: "center",
                        flexWrap: "wrap",
                      }}
                    >
                      <TextField
                        size="small"
                        label="Name"
                        value={editForm.Name}
                        onChange={(event) =>
                          setEditForm((prev) => ({
                            ...prev,
                            Name: event.target.value,
                          }))
                        }
                        sx={{ flex: "1 1 150px", ...fieldSx }}
                      />
                      <TextField
                        size="small"
                        label="Description"
                        value={editForm.Description}
                        onChange={(event) =>
                          setEditForm((prev) => ({
                            ...prev,
                            Description: event.target.value,
                          }))
                        }
                        sx={{ flex: "2 1 200px", ...fieldSx }}
                      />
                      <TextField
                        size="small"
                        label="Sort"
                        type="number"
                        value={editForm.SortOrder}
                        onChange={(event) =>
                          setEditForm((prev) => ({
                            ...prev,
                            SortOrder: event.target.value,
                          }))
                        }
                        sx={{ width: 84, ...fieldSx }}
                      />
                      <Button
                        variant="contained"
                        onClick={() => handleSaveEdit(category.id)}
                        disabled={saving || !editForm.Name.trim()}
                      >
                        Save
                      </Button>
                      <Button
                        color="inherit"
                        onClick={() => setEditingId(null)}
                      >
                        Cancel
                      </Button>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body1" fontWeight={600}>
                          {category.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block" }}
                        >
                          {category.description || "No description"} • Sort{" "}
                          {category.sortOrder ?? 0}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
                        <IconButton
                          size="small"
                          onClick={() => startEdit(category)}
                          aria-label={`Edit ${category.name}`}
                        >
                          <EditRoundedIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(category)}
                          aria-label={`Delete ${category.name}`}
                        >
                          <DeleteOutlineRoundedIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </Box>
    </Overlay>
  );
};

export default DocumentCategoriesManager;
